/**
 * 实时通信中枢对外入口。
 *
 * - 项目内任何需要与服务端实时双向通信的代码，请统一从这里引入 `realtimeHub` 使用，
 *   或通过 `useRealtime` hook 间接使用，而不要直接依赖 `@comein/ws-lib`。
 * - 连接地址通过环境变量 `VITE_WS_URL` 注入；若未配置，hub 不会主动发起连接，
 *   `subscribe` / `send` / `request` 都会在调用时给出一次性的警告。
 * - 鉴权 token 与 jmHttp 一致，经 ws-lib `params` 拼接到连接 URL（浏览器无法设置 WS Header）。
 * - 默认采用 lazy 连接策略，首次 subscribe / send / request 时才建立 WebSocket。
 * - Hub 监听浏览器 `offline`/`online`：断网时尽快进入 `reconnecting`，恢复后防抖重连。
 * - WS 断连/重连失败时若本地 token 已更新（如重新登录），会丢弃旧连接并用新 token 重建。
 *
 * @module utils/realtime
 */

import { RealtimeHub } from './RealtimeHub';
import { resolveRealtimeWsClientOptions } from './wsConnectOptions';
import { emitter } from '@/plugins/emitter';

const LOCAL_STORAGE_WS_URL_KEY = 'VITE_WS_URL';

/**
 * 是否允许从 localStorage 覆盖实时通信 WebSocket 地址。
 * @returns {boolean} 测试环境返回 true
 */
function canUseLocalStorageWsUrl() {
  return String(ENV_DATA.VITE_APP_ENV || '').includes('test');
}

/**
 * 读取测试环境下的本地 WebSocket 地址覆盖配置。
 * @returns {string} localStorage 中配置的 WebSocket 地址，未配置时为空字符串
 */
function getLocalStorageWsUrl() {
  try {
    return String(window.localStorage.getItem(LOCAL_STORAGE_WS_URL_KEY) || '').trim() || ENV_DATA.VITE_WS_URL;
  } catch (error) {
    console.warn('[RealtimeHub] read localStorage VITE_WS_URL failed', error);
    return '';
  }
}

/**
 * 解析实时通信 WebSocket 地址。
 * @returns {string} 测试环境优先使用 localStorage.VITE_WS_URL，否则使用环境变量
 */
function resolveRealtimeWsUrl() {
  return canUseLocalStorageWsUrl() ? getLocalStorageWsUrl() : ENV_DATA.VITE_WS_URL;
}

/**
 * 项目级单例 hub，业务侧默认使用此实例。
 * 如有特殊隔离需求（如多连接、独立断线重连策略），可直接 `new RealtimeHub(...)` 创建独立实例。
 */
export const realtimeHub = new RealtimeHub({
  url: resolveRealtimeWsUrl(),
  connect: 'lazy',
  wsClientOptions: resolveRealtimeWsClientOptions
});

// 重新登录后 token 更新：若 WS 仍持有旧 token 或已重连失败，则主动用新 token 重建连接
emitter.on('login-success', () => {
  realtimeHub.reconnectWithFreshTokenIfNeeded();
});

export { RealtimeHub } from './RealtimeHub';
export { RealtimeSubscriber } from './RealtimeSubscriber';
export { RealtimeStatus, RealtimeEvent, ProtocolField, DEFAULT_REQUEST_TIMEOUT_MS } from './constants';
export { WS_MESSAGE_TYPE } from './wsMessageType';
export { normalizePushData, normalizeStockDetail } from './normalizePushData';
export {
  SecChannelSubscriber,
  getSecChannelSubscriber,
  HeaderDetailChannelSubscriber,
  getHeaderDetailChannelSubscriber,
  installMarketTipsChannel
} from './stockChannels';
